import { useState, useCallback } from "react";
import { TopBar } from "../components/layout/TopBar";
import { SectionLabel } from "../components/shared/SectionLabel";
import { EmptyState } from "../components/shared/EmptyState";
import { Spinner } from "../components/shared/Spinner";
import * as ui from "../styles/ui";
import { api, type Suggestion } from "../api/client";

export function AgentSuggestionsPage() {
  const [docId, setDocId] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[] | null>(null);
  const [proposing, setProposing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (path: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.suggestions.forDoc(path.trim());
      setSuggestions(res.suggestions);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load suggestions",
      );
      setSuggestions(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const propose = useCallback(async () => {
    if (!docId.trim() || proposing) return;
    setProposing(true);
    setError(null);
    try {
      const res = await api.agent.propose(docId.trim());
      setSuggestions(res.suggestions);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Agent proposal failed");
    } finally {
      setProposing(false);
    }
  }, [docId, proposing]);

  const decide = useCallback(
    async (id: number, decision: "accept" | "reject") => {
      setError(null);
      try {
        const res =
          decision === "accept"
            ? await api.suggestions.accept(id)
            : await api.suggestions.reject(id);
        setSuggestions((prev) =>
          (prev ?? []).map((s) => (s.id === id ? res.suggestion : s)),
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : `Failed to ${decision}`);
      }
    },
    [],
  );

  return (
    <>
      <TopBar title="agent suggestions" />
      <div style={ui.pageContent}>
        <SectionLabel>document</SectionLabel>
        <div style={styles.row}>
          <input
            type="text"
            value={docId}
            onChange={(e) => setDocId(e.target.value)}
            placeholder="methodologies/gold-emea/v0.1.md"
            data-testid="agent-doc-input"
            style={styles.input}
          />
          <button
            type="button"
            onClick={() => load(docId)}
            disabled={loading || !docId.trim()}
            style={styles.btn}
          >
            {loading ? "loading…" : "load"}
          </button>
          <button
            type="button"
            onClick={propose}
            disabled={proposing || !docId.trim()}
            data-testid="agent-propose"
            style={styles.primaryBtn}
          >
            {proposing ? "proposing…" : "propose edits"}
          </button>
        </div>

        {error && (
          <div style={styles.error} data-testid="agent-error">
            {error}
          </div>
        )}
        {(loading || proposing) && (
          <Spinner
            size={22}
            label={proposing ? "agent is reviewing…" : "loading…"}
          />
        )}

        {!loading && !proposing && suggestions && suggestions.length === 0 && (
          <EmptyState
            title="no suggestions"
            body="the agent proposed no edits for this document"
          />
        )}

        {!loading && !proposing && suggestions && suggestions.length > 0 && (
          <div data-testid="suggestion-list">
            {suggestions.map((s) => (
              <div key={s.id} style={styles.card}>
                <div style={styles.cardHead}>
                  <span
                    style={{ ...styles.statusTag, ...statusStyle(s.status) }}
                  >
                    {s.status}
                  </span>
                  <span style={styles.source}>{s.source}</span>
                  {s.status === "proposed" && (
                    <div style={styles.actions}>
                      <button
                        type="button"
                        onClick={() => decide(s.id, "accept")}
                        style={styles.acceptBtn}
                      >
                        accept
                      </button>
                      <button
                        type="button"
                        onClick={() => decide(s.id, "reject")}
                        style={styles.rejectBtn}
                      >
                        reject
                      </button>
                    </div>
                  )}
                </div>
                <div style={styles.diff}>
                  <span style={styles.before}>− {s.before}</span>
                  <span style={styles.after}>+ {s.after}</span>
                </div>
                <div style={styles.rationale}>{s.rationale}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function statusStyle(status: Suggestion["status"]): React.CSSProperties {
  if (status === "accepted")
    return { color: "var(--fg-1)", borderColor: "var(--fg-3)" };
  if (status === "rejected")
    return { color: "var(--comp-salmon)", borderColor: "var(--comp-salmon)" };
  return { color: "var(--fg-2)", borderColor: "var(--comp-rule)" };
}

const styles: Record<string, React.CSSProperties> = {
  row: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    margin: "8px 0 16px",
    flexWrap: "wrap" as const,
  },
  input: {
    flex: 1,
    minWidth: 220,
    border: "1px solid var(--comp-rule)",
    padding: "5px 10px",
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    color: "var(--fg-1)",
    background: "transparent",
    outline: "none",
  },
  btn: {
    padding: "5px 12px",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    cursor: "pointer",
    background: "transparent",
    color: "var(--fg-1)",
    border: "1px solid var(--comp-rule)",
  },
  primaryBtn: {
    padding: "5px 14px",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    fontWeight: 500,
    cursor: "pointer",
    background: "var(--comp-ink)",
    color: "var(--adj-white)",
    border: "1px solid var(--comp-ink)",
  },
  error: {
    color: "var(--comp-salmon)",
    fontSize: 12,
    marginBottom: 10,
    fontFamily: "var(--font-mono)",
  },
  card: { border: "1px solid var(--comp-rule)", padding: 12, marginBottom: 8 },
  cardHead: { display: "flex", alignItems: "center", gap: 10, marginBottom: 8 },
  statusTag: {
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    textTransform: "lowercase" as const,
    padding: "1px 6px",
    border: "1px solid var(--comp-rule)",
  },
  source: {
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    color: "var(--fg-mute)",
  },
  actions: { display: "flex", gap: 6, marginLeft: "auto" },
  acceptBtn: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    cursor: "pointer",
    background: "var(--comp-ink)",
    color: "var(--adj-white)",
    border: "1px solid var(--comp-ink)",
    padding: "2px 8px",
  },
  rejectBtn: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    cursor: "pointer",
    background: "transparent",
    color: "var(--comp-salmon)",
    border: "1px solid var(--comp-rule)",
    padding: "2px 8px",
  },
  diff: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    display: "flex",
    flexDirection: "column" as const,
    gap: 2,
    marginBottom: 6,
  },
  before: { color: "var(--comp-salmon)" },
  after: { color: "var(--fg-1)" },
  rationale: {
    fontSize: 12,
    color: "var(--fg-3)",
    fontStyle: "italic" as const,
  },
};
